// src/components/SeoHead.tsx
import { useEffect } from "react";

type SeoHeadProps = {
  title: string;
  description?: string;
  canonicalPath?: string; // ex: "/last-call" (sans domaine)
  image?: string;         // og:image (optionnel)
  noIndex?: boolean;      // optionnel si tu veux bloquer index sur certaines pages
};

function upsertMeta(nameOrProp: "name" | "property", key: string, content: string) {
  if (!content) return;

  const selector = `meta[${nameOrProp}="${key}"]`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(nameOrProp, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  if (!href) return;

  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function SeoHead({
  title,
  description = "",
  canonicalPath,
  image,
  noIndex = false,
}: SeoHeadProps) {
  useEffect(() => {
    // Title
    document.title = title;

    // Description
    if (description) {
      upsertMeta("name", "description", description);
    }

    // Canonical (on enlève les query params par défaut)
    const origin = window.location.origin;
    const path =
      canonicalPath ||
      (window.location.pathname ? window.location.pathname : "/");

    const canonicalUrl = `${origin}${path}`;
    upsertLink("canonical", canonicalUrl);

    // Robots
    if (noIndex) {
      upsertMeta("name", "robots", "noindex, nofollow");
    } else {
      // si tu veux forcer l’indexation normale
      upsertMeta("name", "robots", "index, follow");
    }

    // OpenGraph + Twitter (safe)
    upsertMeta("property", "og:title", title);
    if (description) upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:type", "website");

    if (image) {
      upsertMeta("property", "og:image", image);
      upsertMeta("name", "twitter:card", "summary_large_image");
      upsertMeta("name", "twitter:image", image);
    } else {
      upsertMeta("name", "twitter:card", "summary");
    }

    upsertMeta("name", "twitter:title", title);
    if (description) upsertMeta("name", "twitter:description", description);
  }, [title, description, canonicalPath, image, noIndex]);

  return null;
}
